import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { BackendService } from 'src/app/services/backend.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ApiRequests, components } from 'src/app/shared/types/api.type';

@Component({
  selector: 'app-inventory-declaration-notification-panel',
  templateUrl: './inventory-declaration-notification-panel.component.html',
  styleUrls: ['./inventory-declaration-notification-panel.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatIconModule
  ]
})
export class InventoryDeclarationNotificationPanelComponent implements OnInit {
  @Input({ required: true }) notification!: components['schemas']['InAppNotification'];
  @Output() readonly resolved = new EventEmitter<void>();

  form: FormGroup;
  submitting = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly backendService: BackendService,
    private readonly notify: NotificationService,
    private readonly translate: TranslocoService
  ) {}

  ngOnInit(): void {
    const controls: Record<string, unknown> = {};
    for (const article of this.metadata.articles) {
      controls[`decision_${article.baseItemId}`] = ['accepted', Validators.required];
      controls[`reason_${article.baseItemId}`] = [''];
    }
    this.form = this.fb.group(controls);
  }

  get metadata(): components['schemas']['InventoryDeclarationMetadataDto'] {
    return (
      (this.notification.metadata as unknown as components['schemas']['InventoryDeclarationMetadataDto'] | undefined) ?? {
        userId: 0,
        userName: '',
        status: 'resolved',
        articles: []
      }
    );
  }

  get isAdminReview(): boolean {
    return this.notification.requiresAction && this.metadata.status === 'pending';
  }

  getDecision(baseItemId: number): 'accepted' | 'rejected' {
    return this.form.get(`decision_${baseItemId}`)?.value ?? 'accepted';
  }

  submit(): void {
    if (!this.isAdminReview || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const decisions: ApiRequests['equipment/resolveInventoryDeclaration']['decisions'] = this.metadata.articles.map(
      (article) => {
        const accepted = this.getDecision(article.baseItemId) === 'accepted';
        const rejectionReason = this.form.get(`reason_${article.baseItemId}`)?.value?.trim() ?? '';
        if (!accepted && !rejectionReason) {
          this.form.get(`reason_${article.baseItemId}`)?.setErrors({ required: true });
        }
        return {
          baseItemId: article.baseItemId,
          accepted,
          rejectionReason: accepted ? undefined : rejectionReason
        };
      }
    );

    if (decisions.some((decision) => !decision.accepted && !decision.rejectionReason)) {
      this.notify.error(this.translate.translate('views.psa.inventory_declaration.rejection_reason_required'));
      return;
    }

    this.submitting = true;
    this.backendService
      .post<'equipment/resolveInventoryDeclaration'>('equipment/inventory-declaration/{id}/resolve', {
        path: { id: this.notification.id },
        body: { decisions }
      })
      .subscribe({
        next: () => {
          this.submitting = false;
          this.notify.success(this.translate.translate('views.psa.inventory_declaration.resolved'));
          this.resolved.emit();
        },
        error: () => {
          this.submitting = false;
        }
      });
  }

  getResolvedDecision(baseItemId: number) {
    return this.metadata.decisions?.find((entry) => entry.baseItemId === baseItemId);
  }
}
